import { getStoredUser } from '@shared/auth/auth-route';
import { getTokenClaims } from '@shared/auth/token.store';

import type { PolicyTransition } from './store';
import { behaviourOf, samePolicy, type AiDataPolicy, type AiDataPolicyBehaviour } from './types';

export type SystemNoteKind = 'sn1' | 'sn2' | 'sn3' | 'sn4' | 'sn5' | 'sn6';

export interface SystemNoteSpec {
  kind: SystemNoteKind;
  behaviour: AiDataPolicyBehaviour;
  actorName: string | null;
  at: string | null;
}

const SEEN_KEY = 'investbanq-ai-data-policy-note-seen';

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function key(viewer: string): string {
  return `${SEEN_KEY}:${viewer}`;
}

export function noteViewer(): string {
  return `${getTokenClaims()?.tenantId ?? 'tenant'}:${getStoredUser()?.id ?? 'anon'}`;
}

export function lastSeenBehaviour(viewer: string): AiDataPolicyBehaviour | null {
  try {
    const raw = storage()?.getItem(key(viewer));
    return raw === 'internal' || raw === 'commercial' ? raw : null;
  } catch {
    return null;
  }
}

export function markBehaviourSeen(viewer: string, behaviour: AiDataPolicyBehaviour): void {
  try {
    storage()?.setItem(key(viewer), behaviour);
  } catch {
    return;
  }
}

export function resolveSystemNote(args: {
  policy: AiDataPolicy;
  lastSeen: AiDataPolicyBehaviour | null;
  transition: PolicyTransition | null;
  involvedInternalData: boolean;
}): SystemNoteSpec | null {
  const { policy, lastSeen, transition, involvedInternalData } = args;
  const behaviour = behaviourOf(policy.value);
  if (lastSeen === behaviour) return null;
  const spec = (kind: SystemNoteKind): SystemNoteSpec => ({
    kind,
    behaviour,
    actorName: policy.setBy?.name ?? null,
    at: policy.setAt,
  });

  if (
    transition &&
    samePolicy(transition.current, policy) &&
    behaviourOf(transition.previous.value) !== behaviour
  ) {
    if (behaviour === 'internal') return spec('sn5');
    return spec(transition.previous.value === 'not_chosen' ? 'sn6' : 'sn4');
  }

  if (!involvedInternalData) return null;
  if (policy.value === 'not_chosen') return spec('sn2');
  return spec(policy.value === 'investbanq_only' ? 'sn1' : 'sn3');
}
